// OMS locks an order while an ECPay payment is in progress and refuses a
// second checkout with this error code (redirect `?error=` or POST 400).
export const PAYMENT_IN_PROGRESS_ERROR = 'payment_in_progress'

export interface PaymentLock {
  // Absolute Unix epoch seconds, or null when OMS sent no expiry.
  expiresAt: number | null
}

// OMS sends the lock expiry as absolute Unix epoch seconds (not "seconds
// left"), as `errorParams.paymentLockExpiresAt` on POST or
// `payment_lock_expires_at` on the callback redirect.
export const parsePaymentLockExpiresAt = (value: unknown): number | null => {
  const expiresAt =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof expiresAt === 'number' &&
    Number.isFinite(expiresAt) &&
    expiresAt > 0
    ? expiresAt
    : null
}

// OMS api/v4 cart writes refuse a locked order with
// `400 {errors:[{errorCode:'payment_in_progress', errorParams}]}`. Proxies that
// otherwise flatten every failure to a 500 use this to keep that one code.
export const getPaymentInProgressError = (
  omsErrorBody: unknown,
): {
  status: number
  error: typeof PAYMENT_IN_PROGRESS_ERROR
  errorParams: { paymentLockExpiresAt?: number } | null
} | null => {
  const first = (omsErrorBody as { errors?: unknown[] } | undefined)
    ?.errors?.[0] as
    | { status?: number; errorCode?: string; errorParams?: unknown }
    | undefined
  if (first?.errorCode !== PAYMENT_IN_PROGRESS_ERROR) return null
  return {
    status: first.status || 400,
    error: PAYMENT_IN_PROGRESS_ERROR,
    errorParams:
      (first.errorParams as { paymentLockExpiresAt?: number } | null) ?? null,
  }
}

// cart-api-v3 writes refuse a locked order with HTTP 200 and
// `{result:false, payload:{error:'payment_in_progress', paymentLockExpiresAt}}`;
// the shop proxies wrap that as `{success, data}`. api/v4 writes arrive as an
// RTK error `{status, data:{error, errorParams}}` via the proxies above.
export const getPaymentLockFromResult = (result: {
  data?: unknown
  error?: unknown
}): PaymentLock | null => {
  const payload = (
    result.data as
      | {
          data?: {
            payload?: { error?: string; paymentLockExpiresAt?: unknown }
          }
        }
      | undefined
  )?.data?.payload
  if (payload?.error === PAYMENT_IN_PROGRESS_ERROR) {
    return {
      expiresAt: parsePaymentLockExpiresAt(payload.paymentLockExpiresAt),
    }
  }

  const errorData = (
    result.error as
      | {
          data?: {
            error?: string
            errorParams?: { paymentLockExpiresAt?: unknown } | null
          }
        }
      | undefined
  )?.data
  if (errorData?.error === PAYMENT_IN_PROGRESS_ERROR) {
    return {
      expiresAt: parsePaymentLockExpiresAt(
        errorData.errorParams?.paymentLockExpiresAt,
      ),
    }
  }

  return null
}

// For call sites holding the unwrapped body (`.unwrap()` or `resp.data`):
// true when OMS refused the write because the order is payment-locked. The
// customer is already told by the global toast, so callers just stop.
export const isPaymentLockResponse = (data: unknown): boolean =>
  getPaymentLockFromResult({ data }) !== null

// For catch blocks around `.unwrap()` (api/v4 writes refuse with a 4xx).
export const isPaymentLockError = (error: unknown): boolean =>
  getPaymentLockFromResult({ error }) !== null

export const getPaymentLockSecondsLeft = (
  expiresAt: number,
  nowMs: number = Date.now(),
): number => Math.max(0, Math.ceil(expiresAt - nowMs / 1000))

export const formatPaymentLockCountdown = (secondsLeft: number) => ({
  minutes: String(Math.floor(secondsLeft / 60)),
  seconds: String(secondsLeft % 60).padStart(2, '0'),
})
